/**
 * Weight Freeze (v2) — Requests copy.
 *
 * Verbatim policy text. `PRELIM_HELP` is re-exported from `data/sopCopy.js`'s
 * `POLICY.preliminary` rather than retyped — the SoP page already carries
 * that exact sentence, and two independently-typed copies of a two-cycle
 * rule is exactly the kind of drift that silently goes stale.
 */
import {POLICY} from '../data/sopCopy'
import {MAX_SUBMISSIONS} from './logic'

/** Verbatim, and shared with the SoP page rather than retyped. */
export const PRELIM_HELP = POLICY.preliminary

export const BUDGET_LINE = 'A request gets ' + MAX_SUBMISSIONS + ' submissions in total, being the 1 initial '
  + 'one plus 2 additional rounds. Once all three are used Prozo decides on the evidence already on file.'

export const EXHAUSTED_LINE = 'All ' + MAX_SUBMISSIONS + ' submissions, being the 1 initial one plus 2 '
  + 'additional rounds, are used. Nothing further can be added. Prozo will decide on the evidence '
  + 'already on file.'

/**
 * Why the submit control is unavailable, keyed on the same review sub-state
 * `key` that `stateMeta` returns — so the sentence the merchant reads and the
 * `canSubmit` gate that produced it can never describe two different rules.
 * `exhausted` is the budget case and reuses EXHAUSTED_LINE verbatim.
 */
export const BLOCKED_REASON = {
  awaiting: 'Additional data can only be sent once Prozo asks for it. This request is with Prozo for '
    + 'review and nothing has been asked for, so there is nothing to send yet.',
  submitted: 'Your data is already with Prozo. Nothing further can be sent unless Prozo asks for more.',
  exhausted: EXHAUSTED_LINE,
}

/** Budget wording while sending IS possible — the count doubles as the invitation. */
export const BUDGET_OPEN_LINE = (left) => left + ' of ' + MAX_SUBMISSIONS
  + ' submissions still available on this request.'

/** The same true count while sending is NOT possible, framed as held rather than offered. */
export const BUDGET_HELD_LINE = (left) => left + ' of ' + MAX_SUBMISSIONS + ' submissions unused on this '
  + 'request, held for a round Prozo asks for.'

export const PACK_CONDITION = (label) => 'This freeze holds only while ' + label + ' is the material '
  + 'actually used. Ship the same product in different packing and the frozen weight does not apply to '
  + 'it: that shipment bills on the courier weight instead.'

export const REVIEW_INTRO = 'A request under review sits in one of three sub-states: awaiting Prozo '
  + 'review, additional data requested, or data submitted and awaiting a decision. Each request gets '
  + MAX_SUBMISSIONS + ' submissions in total, being the 1 initial one plus 2 additional rounds. '
  + 'Additional data can only be sent while Prozo has asked for it, so the submit action is available '
  + 'on a request in additional data requested and nowhere else.'

export const REJECTED_INTRO_BOLD = 'Rejected requests are final.'
export const REJECTED_INTRO = ' They cannot be reopened, edited or copied, and an older request cannot '
  + 'be used as the starting point for a new one. The route forward is a fresh request that starts '
  + 'blank, with its own ' + MAX_SUBMISSIONS + '-submission budget.'

export const FRESH_WARN_BOLD = 'A rejected request cannot be reopened, edited or copied.'
export const FRESH_WARN = ' The only route forward is a fresh request, and a fresh request always starts blank.'
export const FRESH_NOT_CARRIED = [
  'The level and the subject. You choose them again.',
  'The dead, volumetric and chargeable weights you asked for.',
  'The packing material the freeze was conditional on.',
  'The DWS evidence that was attached.',
]
export const FRESH_BUDGET = 'The ' + MAX_SUBMISSIONS + '-submission budget resets, so the new request '
  + 'gets 1 initial submission plus 2 additional rounds of its own.'
export const FRESH_ON_FILE = (id) => id + ' stays on file as a record and Prozo can see it during the '
  + 'next review, so the evidence behind the fresh request needs to be genuinely different from what '
  + 'was declined.'

export const NO_FREEZE_BODY = 'No weight has been frozen for this merchant, so there is nothing '
  + 'approved, nothing under review and nothing rejected. Every shipment bills on the weight the '
  + 'courier measures. Start the first request from Initiate weight freeze.'
export const NO_FREEZE_NOTE_BOLD = 'No freeze is a status in its own right.'
export const NO_FREEZE_NOTE = ' It is not an error and it is not a backlog. It means the courier weight '
  + 'is accepted as billed for every shipment, so there is no frozen weight to hold anyone to and no '
  + 'packing-material condition to keep.'

/** The four things a merchant can send on an additional-data submission. */
export const SUBMIT_KINDS = [
  {v: 'dws', l: 'Additional DWS images'},
  {v: 'pack', l: 'Packing material confirmation'},
  {v: 'dims', l: 'Carton dimensions and photographs'},
  {v: 'slip', l: 'Weigh slips from your warehouse'},
]
